import { useEffect, useRef, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import SearchHero from '@/pages/gmb-audit/components/SearchHero';
import SearchForm from '@/pages/gmb-audit/components/SearchForm';
import CandidateSection from '@/pages/gmb-audit/components/CandidateSection';
import SnapshotSection from '@/pages/gmb-audit/components/SnapshotSection';
import PeerPreviewSection from '@/pages/gmb-audit/components/PeerPreviewSection';
import ExecutionDetailsSection from '@/pages/gmb-audit/components/ExecutionDetailsSection';
import ServiceAreaHintSection from '@/pages/gmb-audit/components/ServiceAreaHintSection';
import ComparisonCategorySection from '@/pages/gmb-audit/components/ComparisonCategorySection';
import ScoreSummarySection from '@/pages/gmb-audit/components/ScoreSummarySection';
import type { ScoreStatus } from '@/pages/gmb-audit/components/ScoreSummarySection';
import AuditReportView from '@/pages/gmb-audit/report/AuditReportView';
import type { SaveStatus, ReadbackStatus } from '@/pages/gmb-audit/report/components/ReportSaveSection';
import StepsSection from '@/pages/gmb-audit/components/StepsSection';
import DataLimitSection from '@/pages/gmb-audit/components/DataLimitSection';
import StateNotice from '@/pages/gmb-audit/components/StateNotice';
import {
  searchCandidatePlaces,
  fetchPlaceSnapshot,
  fetchPeers,
  calculateAuditScore,
  saveGmbAudit,
  getGmbAuditResult,
} from '@/pages/gmb-audit/data/auditApi';
import {
  resolveComparisonCategory,
  buildUserConfirmedCategory,
} from '@/pages/gmb-audit/utils/comparisonCategory';
import { getHistoryScopeToken } from '@/pages/gmb-audit/utils/historyScope';
import type {
  AuditState,
  AuditScoreResult,
  CandidatePlace,
  ComparisonCategory,
  ComparisonCategoryMode,
  GmbAuditReport,
  PeerSearchPlace,
  PeerSearchResult,
  PlaceSnapshot,
  SampleConfidence,
} from '@/types/gmb';

// 後端 error code → 前端狀態分流（僅在無法取得真實資料時使用）。
const ERROR_CODE_TO_STATE: Record<string, AuditState> = {
  GOOGLE_PLACES_QUOTA_EXCEEDED: 'quota-error',
  GOOGLE_PLACES_BAD_REQUEST: 'search-invalid',
  INVALID_INPUT: 'search-invalid',
  // Security-B1｜Kill Switch：不做自動 retry，僅顯示友善提示（使用者自行重試才會是新請求）。
  GMB_AUDIT_RESTRICTED: 'api-error',
  GMB_AUDIT_MAINTENANCE: 'api-error',
  // Security-C1｜Global rate limit：不自動 retry、不 loop retry；顯示「目前使用人數較多」。
  RATE_LIMITED: 'quota-error',
  RATE_LIMIT_UNAVAILABLE: 'api-error',
  // Security-C1.1｜Daily Google cost ceiling：今日額度用盡 → 「請明日再試」，不自動 retry。
  DAILY_LIMIT_REACHED: 'daily-limit',
  DAILY_LIMIT_UNAVAILABLE: 'api-error',
};

/** 計分 error code → 一般人可理解的訊息（不外洩 stack / 內部細節）。 */
function mapScoreError(code: string, message: string): string {
  if (code === 'GMB_AUDIT_RESTRICTED') {
    return '目前暫停新的 Google 商家健診，請稍後再試。已保存的健診報告仍可正常查看。';
  }
  if (code === 'GMB_AUDIT_MAINTENANCE') {
    return '系統目前正在維護，暫時無法進行新的健診，請稍後再試。';
  }
  if (code === 'SCORING_INVARIANT_FAILED') {
    return '健診分數計算發生資料一致性問題，請重新執行健診。';
  }
  if (code === 'INVALID_INPUT') {
    return '健診資料不完整，請重新搜尋商家後再試一次。';
  }
  return message || '健診分數計算暫時無法完成，請稍後再試。';
}

/** Batch 7-B2A：為「每一份完成的 AuditReport」產生一次 saveRequestId（同一份報告重試沿用同一個）。 */
function createSaveRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to manual v4 generation
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

/** 保存 error code → 一般人可理解的訊息（不顯示 raw DB error）。 */
function mapSaveError(code: string): string {
  switch (code) {
    case 'SERVER_DB_CREDENTIAL_UNAVAILABLE':
      return '目前無法連線至報告保存服務，請稍後再試。';
    case 'AUDIT_SAVE_INCOMPLETE':
      return '此份報告的保存狀態異常，請稍後再試。';
    case 'IDEMPOTENCY_CONFLICT':
      return '報告保存識別資料不一致，請重新執行健診。';
    case 'AUDIT_SAVE_IN_PROGRESS':
      return '報告仍在保存中，請稍後再試。';
    case 'GMB_AUDIT_MAINTENANCE':
      return '系統目前正在維護，暫時無法儲存新的健診報告，請稍後再試。';
    case 'GMB_AUDIT_RESTRICTED':
      return '目前暫停新的健診服務，請稍後再試。已保存的健診報告仍可正常查看。';
    case 'RATE_LIMITED':
      return '目前使用人數較多，請稍後再試。';
    case 'RATE_LIMIT_UNAVAILABLE':
      return '服務暫時無法使用，請稍後再試。';
    default:
      return '報告保存失敗，請重新嘗試。';
  }
}

/** 讀取已保存報告 error code → 一般人可理解的訊息（不顯示 raw DB error / internal audit id）。 */
function mapReadbackError(code: string): string {
  switch (code) {
    case 'INVALID_REPORT_TOKEN':
      return '報告識別資料格式錯誤。';
    case 'REPORT_NOT_FOUND':
      return '找不到這份已保存的健診報告。';
    case 'REPORT_DATA_INTEGRITY_ERROR':
      return '這份報告的保存資料不完整，暫時無法開啟。';
    case 'REPORT_DATA_INVALID':
      return '這份報告的資料格式異常，暫時無法開啟。';
    case 'REPORT_SCHEMA_UNSUPPORTED':
      return '這份報告使用較舊或尚未支援的資料版本。';
    case 'SERVER_DB_CREDENTIAL_UNAVAILABLE':
      return '目前無法連線至報告讀取服務，請稍後再試。';
    case 'RATE_LIMITED':
      return '目前使用人數較多，請稍後再試。';
    case 'RATE_LIMIT_UNAVAILABLE':
      return '服務暫時無法使用，請稍後再試。';
    default:
      return '讀取報告失敗，請稍後再試。';
  }
}

interface RunPeerSearchOptions {
  /** 本次 audit execution chain 的序號（用於 stale async guard） */
  runId: number;
  /** GOOGLE_TYPE 路徑使用的比較類型（GOOGLE_PRIMARY_TYPE / GOOGLE_TYPES_FALLBACK） */
  comparisonType?: string;
  /** 服務區域比對範圍（Service Area） */
  serviceAreaHint?: string;
  /** USER_CONFIRMED 的使用者確認關鍵字（Keyword Text Search） */
  comparisonKeyword?: string;
  /** 分類來源（供後端判斷 fallback type 是否可作 Google filter） */
  categoryMode?: ComparisonCategoryMode;
  /** 本次搜尋所依據的同業分類（計分時使用；不得讀取尚未 commit 的 state） */
  category?: ComparisonCategory;
}

interface RunScoringOptions {
  /** 本次計分所依據的已解析 Snapshot（resolved，不依賴 React state） */
  targetSnapshot: PlaceSnapshot;
  /** 本次計分所依據的已解析同業分類（resolved） */
  comparisonCategory: ComparisonCategory;
  /** 本次計分所依據的已解析同業統計（resolvedPeerResult） */
  peerComparison: PeerSearchResult;
  /** 本次 audit execution chain 的序號（用於 stale async guard） */
  runId: number;
}

export default function GmbAuditPage() {
  const [state, setState] = useState<AuditState>('initial');
  const [candidates, setCandidates] = useState<CandidatePlace[]>([]);
  const [selected, setSelected] = useState<CandidatePlace | null>(null);
  const [snapshot, setSnapshot] = useState<PlaceSnapshot | null>(null);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  // 使用者第一階段輸入的城市／行政區（作為 Service Area 同業搜尋的 serviceAreaHint 來源）。
  const [searchRegion, setSearchRegion] = useState('');

  // 同業分類決策層（Batch 5-D1）
  const [comparisonCategory, setComparisonCategory] = useState<ComparisonCategory | null>(null);
  // 目前是否需要使用者確認同業分類（USER_CONFIRMATION_REQUIRED）
  const [categoryConfirmationRequired, setCategoryConfirmationRequired] = useState(false);

  // 同業搜尋（Batch 5-A：一般實體 Nearby；Batch 5-B：Service Area Text Search；Batch 5-D2：Keyword Text Search）
  const [peerLoading, setPeerLoading] = useState(false);
  const [peerLoadingText, setPeerLoadingText] = useState('正在比對附近同業…');
  const [peers, setPeers] = useState<PeerSearchPlace[] | null>(null);
  const [peerMode, setPeerMode] = useState<string | null>(null);
  const [peerSelectionMode, setPeerSelectionMode] = useState<string | null>(null);
  const [peerRadiusUsedM, setPeerRadiusUsedM] = useState<number | null>(null);
  const [peerComparisonArea, setPeerComparisonArea] = useState<string | null>(null);
  // 同業樣本統計（Batch 5-C）
  const [peerSampleCount, setPeerSampleCount] = useState<number | null>(null);
  const [peerConfidence, setPeerConfidence] = useState<SampleConfidence | null>(null);
  const [peerRatingSampleCount, setPeerRatingSampleCount] = useState<number | null>(null);
  const [peerReviewCountSampleCount, setPeerReviewCountSampleCount] = useState<number | null>(null);
  const [peerRatingMedian, setPeerRatingMedian] = useState<number | null>(null);
  const [peerReviewCountMedian, setPeerReviewCountMedian] = useState<number | null>(null);
  // 完整同業結果（供計分與 6-B2 報告組裝；不落 DB）
  const [peerResult, setPeerResult] = useState<PeerSearchResult | null>(null);
  const [peerError, setPeerError] = useState<string | null>(null);
  const [peerNotice, setPeerNotice] = useState<string | null>(null);
  // Service Area Business 尚未有 serviceAreaHint 時，顯示輸入欄。
  const [peerHintRequired, setPeerHintRequired] = useState(false);

  // 計分狀態（Batch 6-B1）
  const [scoreStatus, setScoreStatus] = useState<ScoreStatus>('idle');
  const [auditScoreResult, setAuditScoreResult] = useState<AuditScoreResult | null>(null);
  const [scoreError, setScoreError] = useState<string | null>(null);
  // 前端 runtime 報告物件（僅供 6-B2；不存 DB、不產生 reportId）
  const [auditReport, setAuditReport] = useState<GmbAuditReport | null>(null);

  // ---------------------------------------------------------------------------
  // Batch 7-B2A｜手動保存報告狀態（saveRequestId 綁定「目前這一份 auditReport」）
  // ---------------------------------------------------------------------------
  const [saveRequestId, setSaveRequestId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveReused, setSaveReused] = useState(false);
  // 保存成功後的 reportToken（本批僅保存在 state，暫不建立分享 URL）
  const [reportToken, setReportToken] = useState<string | null>(null);
  // Batch 7-C：匿名 History Scope（同一 browser + 同一 placeId 永遠沿用同一個 UUID）。
  const [historyScopeToken, setHistoryScopeToken] = useState<string | null>(null);
  // 目前 saveRequestId 所屬的 report identity（用於保存後的 stale guard）。
  const reportIdentityRef = useRef<GmbAuditReport | null>(null);

  // ---------------------------------------------------------------------------
  // Batch 7-B2B｜歷史 Snapshot 讀回（get_gmb_audit_result）
  // live auditReport 與 saved retrievedReport 完全分開：DB response 絕不覆蓋 live report。
  // ---------------------------------------------------------------------------
  const [savedReport, setSavedReport] = useState<GmbAuditReport | null>(null);
  const [savedReportMeta, setSavedReportMeta] = useState<{
    reportToken: string;
    createdAt: string;
    schemaVersion: number;
  } | null>(null);
  const [readbackStatus, setReadbackStatus] = useState<ReadbackStatus>('idle');
  const [readbackError, setReadbackError] = useState<string | null>(null);
  // 目前顯示的是本次即時報告（live）或已保存的歷史報告（saved）。
  const [viewMode, setViewMode] = useState<'live' | 'saved'>('live');

  // ---------------------------------------------------------------------------
  // Stale async request guard（Batch 6-B1.1）
  // 每次「新的 audit execution chain」都會遞增序號；任何 await 之後都必須比對，
  // 確保商家 A 晚到的 response 不會覆蓋使用者後來搜尋的商家 B。
  // ---------------------------------------------------------------------------
  const auditRunRef = useRef(0);

  // 報告自動定位（Batch 6-B2.1）：同一份 auditReport 只自動捲動一次。
  const reportRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledReportRef = useRef<GmbAuditReport | null>(null);

  /** 開始一條新的 audit chain，回傳本次 runId（同時使所有舊的 in-flight 請求失效）。 */
  const beginAuditRun = (): number => {
    auditRunRef.current += 1;
    return auditRunRef.current;
  };

  /** 這次 runId 是否仍是目前有效的 chain。 */
  const isRunActive = (runId: number): boolean => auditRunRef.current === runId;

  /** 清除計分相關狀態（不影響 Snapshot / 選店）。 */
  const resetScoring = () => {
    setScoreStatus('idle');
    setAuditScoreResult(null);
    setScoreError(null);
    setAuditReport(null);
  };

  const resetPeers = () => {
    setPeerLoading(false);
    setPeers(null);
    setPeerMode(null);
    setPeerSelectionMode(null);
    setPeerRadiusUsedM(null);
    setPeerComparisonArea(null);
    setPeerSampleCount(null);
    setPeerConfidence(null);
    setPeerRatingSampleCount(null);
    setPeerReviewCountSampleCount(null);
    setPeerRatingMedian(null);
    setPeerReviewCountMedian(null);
    setPeerResult(null);
    setPeerError(null);
    setPeerNotice(null);
    setPeerHintRequired(false);
    setComparisonCategory(null);
    setCategoryConfirmationRequired(false);
    resetScoring();
  };

  const handleSearch = async (name: string, region: string) => {
    // 新搜尋 = 新的 chain：立即讓前一個商家的 in-flight snapshot / peer / score 全部失效。
    const runId = beginAuditRun();
    setState('searching');
    setCandidates([]);
    setSelected(null);
    setSnapshot(null);
    setSnapshotError(null);
    setSearchRegion(region);
    resetPeers();
    try {
      const result = await searchCandidatePlaces(name, region);
      if (!isRunActive(runId)) return; // 已被更新的搜尋取代
      if (!result.ok) {
        setState(ERROR_CODE_TO_STATE[result.code] ?? 'api-error');
        return;
      }
      if (result.places.length === 0) {
        setState('no-results');
        return;
      }
      setCandidates(result.places.slice(0, 5));
      setState('candidate-results');
    } catch {
      if (!isRunActive(runId)) return;
      setState('api-error');
    }
  };

  /**
   * 執行計分：只使用「本條 chain 已解析」的 Snapshot / ComparisonCategory / PeerComparison。
   * 不重新呼叫任何 Google API（search / snapshot / peers）；不讀取尚未 commit 的 React state。
   */
  const runScoring = async ({
    targetSnapshot,
    comparisonCategory: category,
    peerComparison: peerStats,
    runId,
  }: RunScoringOptions) => {
    // Guard：分類尚未確認時不得計分（M02=0 只保留 Backend 防禦）。
    if (category.mode === 'USER_CONFIRMATION_REQUIRED') return;
    // Guard：此 chain 已失效（使用者已重搜 / 換分類）→ 不得計分。
    if (!isRunActive(runId)) return;

    setScoreStatus('loading');
    setScoreError(null);
    setAuditScoreResult(null);
    setAuditReport(null);
    try {
      const res = await calculateAuditScore({
        targetSnapshot,
        comparisonCategory: category,
        peerComparison: {
          sampleCount: peerStats.sampleCount,
          ratingSampleCount: peerStats.ratingSampleCount,
          reviewCountSampleCount: peerStats.reviewCountSampleCount,
          ratingMedian: peerStats.ratingMedian,
          reviewCountMedian: peerStats.reviewCountMedian,
        },
      });
      // 晚到的計分結果：此 chain 已失效 → 丟棄，不寫入任何 state、不建立 AuditReport。
      if (!isRunActive(runId)) return;
      if (!res.ok) {
        setScoreError(mapScoreError(res.code, res.message));
        setScoreStatus('error');
        return;
      }
      setAuditScoreResult(res.result);
      setScoreStatus('success');
      // AuditReport 只在同一條有效 chain 內組裝（target / category / peers / score 同源）。
      setAuditReport({
        target: targetSnapshot,
        comparisonCategory: category,
        peerComparison: peerStats,
        scoreResult: res.result,
      });
    } catch {
      if (!isRunActive(runId)) return;
      setScoreError('健診分數計算暫時無法完成，請稍後再試。');
      setScoreStatus('error');
    }
  };

  // 同業搜尋共用入口：
  // - GOOGLE_TYPE：Nearby（一般實體）/ Service Area（Text Search + hint）
  // - KEYWORD_TEXT_SEARCH：USER_CONFIRMED keyword（一般實體 + locationBias / 服務區域 + hint）
  const runPeerSearch = async (snap: PlaceSnapshot, options: RunPeerSearchOptions) => {
    const { runId } = options;
    if (!isRunActive(runId)) return;

    const isService = snap.pureServiceAreaBusiness === true;
    setPeerLoadingText(
      options.comparisonKeyword
        ? '正在建立同業樣本…'
        : isService
          ? '正在建立服務區域同業樣本…'
          : '正在比對附近同業…',
    );
    setPeerLoading(true);
    setPeers(null);
    setPeerMode(null);
    setPeerSelectionMode(null);
    setPeerRadiusUsedM(null);
    setPeerComparisonArea(null);
    setPeerSampleCount(null);
    setPeerConfidence(null);
    setPeerRatingSampleCount(null);
    setPeerReviewCountSampleCount(null);
    setPeerRatingMedian(null);
    setPeerReviewCountMedian(null);
    setPeerResult(null);
    setPeerError(null);
    setPeerNotice(null);
    setPeerHintRequired(false);
    resetScoring();

    // 只有「取得合法 PeerSearchResult」時才會被賦值；error / 提前 return 皆維持 null。
    // 本函式不使用 finally 判定成功，finally 只負責關閉 loading。
    let resolvedPeerResult: PeerSearchResult | null = null;

    try {
      const result = await fetchPeers({
        placeId: snap.placeId,
        latitude: snap.latitude,
        longitude: snap.longitude,
        primaryType: snap.primaryType,
        primaryTypeDisplayName: snap.primaryTypeDisplayName,
        types: snap.types,
        comparisonType: options.comparisonType,
        comparisonKeyword: options.comparisonKeyword,
        comparisonCategoryMode: options.categoryMode,
        pureServiceAreaBusiness: snap.pureServiceAreaBusiness,
        serviceAreaHint: options.serviceAreaHint,
      });
      // 晚到的 peer response：此 chain 已失效 → 不得覆蓋新商家的狀態。
      if (!isRunActive(runId)) return;

      if (!result.ok) {
        // 服務區域商家尚未提供範圍 → 改為顯示輸入欄，而非錯誤。
        if (result.code === 'PEER_SEARCH_AREA_REQUIRED') {
          setPeerHintRequired(true);
          return;
        }
        // 分類仍不足（fallback type 不可作 filter 或太泛用）→ 顯示「確認主要同業分類」。
        if (result.code === 'PEER_CATEGORY_CONFIRMATION_REQUIRED') {
          setCategoryConfirmationRequired(true);
          return;
        }
        // 其他 peer request failure（HTTP error / INTERNAL_ERROR / upstream error / 格式錯誤）
        // → 維持 peer-error，不進 Scoring（resolvedPeerResult 仍為 null）。
        setPeerError(result.message);
        return;
      }

      // 合法 PeerSearchResult（含 sampleCount = 0 的 valid zero-peer，仍屬正常完成）。
      setPeers(result.peers);
      setPeerMode(result.mode);
      setPeerSelectionMode(result.selectionMode);
      setPeerRadiusUsedM(result.radiusUsedM);
      setPeerComparisonArea(result.comparisonArea);
      setPeerSampleCount(result.sampleCount);
      setPeerConfidence(result.sampleConfidence);
      setPeerRatingSampleCount(result.ratingSampleCount);
      setPeerReviewCountSampleCount(result.reviewCountSampleCount);
      setPeerRatingMedian(result.ratingMedian);
      setPeerReviewCountMedian(result.reviewCountMedian);

      resolvedPeerResult = {
        mode: result.mode,
        selectionMode: result.selectionMode ?? undefined,
        comparisonCategoryLabel: result.comparisonCategoryLabel ?? undefined,
        comparisonCategorySource: result.comparisonCategorySource ?? undefined,
        radiusUsedM: result.radiusUsedM ?? undefined,
        comparisonArea: result.comparisonArea ?? undefined,
        sampleCount: result.sampleCount,
        sampleConfidence: result.sampleConfidence,
        ratingSampleCount: result.ratingSampleCount,
        reviewCountSampleCount: result.reviewCountSampleCount,
        ratingMedian: result.ratingMedian ?? undefined,
        reviewCountMedian: result.reviewCountMedian ?? undefined,
        peers: result.peers,
      };
      setPeerResult(resolvedPeerResult);
    } catch {
      if (!isRunActive(runId)) return;
      setPeerError('服務暫時無法使用，請稍後再試。');
    } finally {
      // finally 只允許關閉 loading；不得在此判定 Peer Search 成功。
      if (isRunActive(runId)) setPeerLoading(false);
    }

    // Scoring 觸發條件：本次 chain 仍有效 + 取得合法 PeerSearchResult（含 0 peer）
    // + 已解析的分類且非 USER_CONFIRMATION_REQUIRED。
    // 使用 resolvedPeerResult（非等待 React state），並沿用同一條 chain 的 runId。
    const resolvedCategory = options.category;
    if (
      resolvedPeerResult &&
      resolvedCategory &&
      resolvedCategory.mode !== 'USER_CONFIRMATION_REQUIRED'
    ) {
      await runScoring({
        targetSnapshot: snap,
        comparisonCategory: resolvedCategory,
        peerComparison: resolvedPeerResult,
        runId,
      });
    }
  };

  // 選擇候選店家：呼叫 get_google_place_snapshot，成功後由決策層決定同業搜尋或要求確認。
  const handleSelect = async (candidate: CandidatePlace) => {
    if (state === 'snapshot-loading') return; // 防止重複點擊
    const runId = beginAuditRun();
    setSelected(candidate);
    setSnapshot(null);
    setSnapshotError(null);
    resetPeers();
    setState('snapshot-loading');
    try {
      const result = await fetchPlaceSnapshot(candidate.placeId);
      if (!isRunActive(runId)) return;
      if (!result.ok) {
        setSnapshotError(result.message);
        setState('snapshot-error');
        return;
      }
      // resolvedSnapshot：後續 Category / Peer / Scoring 全部沿用本次解析值。
      const resolvedSnapshot = result.snapshot;
      setSnapshot(resolvedSnapshot);
      setState('snapshot-ready');

      // 同業分類決策層（deterministic；不呼叫 AI、不猜產業）。
      const resolvedCategory = resolveComparisonCategory(resolvedSnapshot);
      setComparisonCategory(resolvedCategory);

      const isServiceArea = resolvedSnapshot.pureServiceAreaBusiness === true;

      // 分類太泛用 → 要求使用者確認，不執行 Peer Search、不計分。
      if (resolvedCategory.needsConfirmation) {
        setCategoryConfirmationRequired(true);
        return;
      }

      // Service Area Business：有 serviceAreaHint 才自動搜尋，否則顯示輸入欄。
      if (isServiceArea) {
        const hint = searchRegion.trim();
        if (hint) {
          await runPeerSearch(resolvedSnapshot, {
            runId,
            comparisonType: resolvedCategory.comparisonType,
            serviceAreaHint: hint,
            categoryMode: resolvedCategory.mode,
            category: resolvedCategory,
          });
        } else {
          setPeerHintRequired(true);
        }
        return;
      }

      // 一般實體商家：需有座標與已解析的比較類型才能建立可靠同業比較。
      if (
        typeof resolvedSnapshot.latitude === 'number' &&
        typeof resolvedSnapshot.longitude === 'number' &&
        resolvedCategory.comparisonType
      ) {
        await runPeerSearch(resolvedSnapshot, {
          runId,
          comparisonType: resolvedCategory.comparisonType,
          categoryMode: resolvedCategory.mode,
          category: resolvedCategory,
        });
        return;
      }
      setPeerNotice('Google 公開資料未提供足夠的位置或類型資訊，暫時無法建立同業比較。');
    } catch {
      if (!isRunActive(runId)) return;
      setSnapshotError('服務暫時無法使用，請稍後再試。');
      setState('snapshot-error');
    }
  };

  // 使用者確認同業分類後：建立 USER_CONFIRMED，並「立即」以 keyword 建立同業樣本。
  // 一般實體 → Physical Keyword Text Search；服務區域 → 有 hint 就搜尋，否則等使用者補填。
  // 不重新搜尋店家、不重新取得 Place Details。搜尋完成即計分。
  const handleCategoryConfirm = (payload: {
    comparisonKeyword: string;
    serviceAreaHint?: string;
  }) => {
    const runId = beginAuditRun();
    const resolvedCategory = buildUserConfirmedCategory(payload.comparisonKeyword);
    setComparisonCategory(resolvedCategory);
    setCategoryConfirmationRequired(false);
    setPeerNotice(null);

    if (!snapshot) return;

    const isServiceArea = snapshot.pureServiceAreaBusiness === true;
    if (isServiceArea) {
      const hint = (payload.serviceAreaHint ?? searchRegion).trim();
      if (hint) {
        setSearchRegion(hint);
        runPeerSearch(snapshot, {
          runId,
          comparisonKeyword: resolvedCategory.comparisonKeyword,
          serviceAreaHint: hint,
          category: resolvedCategory,
        });
      } else {
        setPeerHintRequired(true);
      }
      return;
    }

    runPeerSearch(snapshot, {
      runId,
      comparisonKeyword: resolvedCategory.comparisonKeyword,
      category: resolvedCategory,
    });
  };

  // 修改同業分類：保留 Snapshot / 選店，只清除 Peer 與計分結果後重新輸入分類（不重新搜尋店家）。
  const handleModifyCategory = () => {
    // 讓先前 chain 的 in-flight peer / score 失效，避免舊結果晚到後回流。
    beginAuditRun();
    setPeers(null);
    setPeerMode(null);
    setPeerSelectionMode(null);
    setPeerRadiusUsedM(null);
    setPeerComparisonArea(null);
    setPeerSampleCount(null);
    setPeerConfidence(null);
    setPeerRatingSampleCount(null);
    setPeerReviewCountSampleCount(null);
    setPeerRatingMedian(null);
    setPeerReviewCountMedian(null);
    setPeerResult(null);
    setPeerError(null);
    setPeerNotice(null);
    setPeerHintRequired(false);
    resetScoring();
    setCategoryConfirmationRequired(true);
  };

  // 計分失敗重試：只用目前 state 重算，不重新取得 Google 資料（開啟新 chain）。
  const handleRetryScoring = () => {
    if (
      snapshot &&
      comparisonCategory &&
      peerResult &&
      comparisonCategory.mode !== 'USER_CONFIRMATION_REQUIRED'
    ) {
      const runId = beginAuditRun();
      runScoring({
        targetSnapshot: snapshot,
        comparisonCategory,
        peerComparison: peerResult,
        runId,
      });
    }
  };

  // Batch 7-B2A：使用者主動點擊才保存（絕不自動保存）。同一份報告重試沿用同一 saveRequestId。
  const handleSaveReport = async () => {
    const currentReport = auditReport;
    if (!currentReport || !saveRequestId || !historyScopeToken) return;
    if (saveStatus === 'loading') return; // UX 防連點（不是 security control）
    setSaveStatus('loading');
    setSaveError(null);
    const res = await saveGmbAudit({
      targetSnapshot: currentReport.target,
      comparisonCategory: currentReport.comparisonCategory,
      peerComparison: currentReport.peerComparison ?? null,
      scoreResult: currentReport.scoreResult,
      saveRequestId,
      historyScopeToken,
    });
    // stale guard：若等待期間報告已被重置 / 更換（例如按了重新健診），丟棄本次結果。
    if (reportIdentityRef.current !== currentReport) return;
    if (res.ok) {
      setReportToken(res.data.reportToken);
      setSaveReused(res.data.reused);
      setSaveStatus('success');
      return;
    }
    setSaveStatus('error');
    setSaveError(mapSaveError(res.code));
  };

  // Batch 7-B2B / 7-C：以 reportToken 讀回歷史 Snapshot（讀取已保存報告 / 歷史清單「查看該次報告」共用）。
  // 只做 Database readback；不重新健診、不重新計算、不呼叫 Google。
  const openSavedReport = async (token: string) => {
    const currentReport = auditReport;
    if (!currentReport || !token) return;
    if (readbackStatus === 'loading') return;
    setReadbackStatus('loading');
    setReadbackError(null);
    const res = await getGmbAuditResult(token);
    // stale guard：等待期間報告已被重置 / 更換 → 丟棄結果。
    if (reportIdentityRef.current !== currentReport) return;
    if (res.ok) {
      setSavedReport(res.data.report);
      setSavedReportMeta({
        reportToken: res.data.reportToken,
        createdAt: res.data.createdAt,
        schemaVersion: res.data.schemaVersion,
      });
      setReadbackStatus('idle');
      setViewMode('saved');
      return;
    }
    setReadbackStatus('error');
    setReadbackError(mapReadbackError(res.code));
  };

  // 使用者主動點擊「讀取已保存報告」→ 以目前 state 的 reportToken 讀回。
  const handleReadback = () => {
    if (reportToken) openSavedReport(reportToken);
  };

  // 歷史清單「查看該次報告」→ 以該次 reportToken 讀回。
  const handleOpenReportFromHistory = (token: string) => {
    openSavedReport(token);
  };

  // 從歷史報告返回本次即時報告（不影響 live auditReport / runId / save state）。
  const handleReturnToLive = () => setViewMode('live');

  // 重新健診其他商家（Batch 6-B2，不重新載入頁面）：
  // 使舊 runId 失效並清除 selectedPlace / snapshot / comparisonCategory / peer / score / report，
  // 回到搜尋區。
  const handleRestart = () => {
    beginAuditRun();
    setState('initial');
    setCandidates([]);
    setSelected(null);
    setSnapshot(null);
    setSnapshotError(null);
    setSearchRegion('');
    resetPeers();
  };

  const isServiceArea = snapshot?.pureServiceAreaBusiness === true;
  const hasReport = scoreStatus === 'success' && auditReport !== null;
  // live / saved 報告分離：saved 模式且有 savedReport 時顯示歷史報告，否則顯示即時報告。
  const displayedReport = viewMode === 'saved' && savedReport ? savedReport : auditReport;

  // 新的 auditReport 第一次建立完成時，自動平滑捲動到正式報告一次。
  // 以 auditReport 物件 identity 為 key → 同一份 report 不會重複捲動，
  // 使用者手動捲動後也不會被拉回。
  useEffect(() => {
    if (!auditReport) return;
    if (lastScrolledReportRef.current === auditReport) return;
    lastScrolledReportRef.current = auditReport;
    const id = window.requestAnimationFrame(() => {
      reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [auditReport]);

  // Batch 7-B2A：每當產生「新的 auditReport」→ 產生全新的 saveRequestId 並重設保存狀態。
  // 同一份報告的 retry / 再次點擊 → 沿用同一 saveRequestId；report 變成 null（重新健診 / 改分類）→ 全面清除。
  // Batch 7-B2B：同時清除「歷史讀回」相關狀態，避免舊歷史資料殘留／覆蓋即時報告。
  useEffect(() => {
    reportIdentityRef.current = auditReport;
    // 任何 report identity 變動都先回到 live 檢視，並清除歷史讀回結果。
    setSavedReport(null);
    setSavedReportMeta(null);
    setReadbackStatus('idle');
    setReadbackError(null);
    setViewMode('live');
    if (!auditReport) {
      setSaveRequestId(null);
      setSaveStatus('idle');
      setSaveError(null);
      setSaveReused(false);
      setReportToken(null);
      setHistoryScopeToken(null);
      return;
    }
    setSaveRequestId(createSaveRequestId());
    setSaveStatus('idle');
    setSaveError(null);
    setSaveReused(false);
    setReportToken(null);
    // Batch 7-C：同一 placeId 永遠沿用同一個 historyScopeToken（不同商家各自獨立）。
    setHistoryScopeToken(getHistoryScopeToken(auditReport.target.placeId));
  }, [auditReport]);

  // 同業區塊（依分類確認 / 服務區域提示 / 一般同業三種狀態）
  const peerArea =
    categoryConfirmationRequired ? (
      <ComparisonCategorySection
        showServiceAreaField={isServiceArea === true && !searchRegion.trim()}
        defaultServiceAreaHint={searchRegion}
        loading={peerLoading}
        onSubmit={handleCategoryConfirm}
      />
    ) : peerHintRequired ? (
      <ServiceAreaHintSection
        loading={peerLoading}
        defaultHint={searchRegion}
        onSubmit={(hint) => {
          if (!snapshot) return;
          const runId = beginAuditRun();
          runPeerSearch(snapshot, {
            runId,
            comparisonType: comparisonCategory?.comparisonType,
            comparisonKeyword: comparisonCategory?.comparisonKeyword,
            categoryMode: comparisonCategory?.mode,
            serviceAreaHint: hint,
            category: comparisonCategory ?? undefined,
          });
        }}
      />
    ) : (
      <PeerPreviewSection
        loading={peerLoading}
        loadingMessage={peerLoadingText}
        peers={peers}
        mode={peerMode}
        selectionMode={peerSelectionMode}
        radiusUsedM={peerRadiusUsedM}
        comparisonArea={peerComparisonArea}
        sampleCount={peerSampleCount}
        sampleConfidence={peerConfidence}
        ratingSampleCount={peerRatingSampleCount}
        reviewCountSampleCount={peerReviewCountSampleCount}
        ratingMedian={peerRatingMedian}
        reviewCountMedian={peerReviewCountMedian}
        comparisonCategory={comparisonCategory}
        onModifyCategory={handleModifyCategory}
        errorMessage={peerError}
        noticeMessage={peerNotice}
      />
    );

  // 執行過程型資訊（報告完成後會被收合成預設 collapsed 的區塊）。
  const executionContent = (
    <>
      <StateNotice state={state} />
      <CandidateSection state={state} candidates={candidates} selected={selected} onSelect={handleSelect} />
      <SnapshotSection
        loading={state === 'snapshot-loading'}
        snapshot={snapshot}
        errorMessage={snapshotError}
        candidate={selected}
      />
      {peerArea}
    </>
  );

  return (
    <SiteLayout>
      <SearchHero />

      {/* 搜尋表單（報告完成後視覺降階，正式報告成為主要焦點；表單仍可保留使用）。 */}
      <div className={`transition-opacity ${hasReport ? 'opacity-95' : ''}`}>
        <SearchForm onSubmit={handleSearch} loading={state === 'searching'} />
      </div>

      {/* 輕量公開資料 Info Bar（位於搜尋區下方，不搶主視覺）。 */}
      {!hasReport && (
        <div className="ds-container pt-6 md:pt-8">
          <div className="mx-auto flex max-w-search flex-wrap items-center justify-center gap-2.5 rounded-2xl border border-background-300 bg-background-100 px-5 py-4 text-center md:rounded-full md:py-3">
            <i className="ri-google-fill text-xl text-foreground-500 md:text-base" />
            <p className="text-base leading-relaxed text-foreground-600">
              使用 Google Maps / Places 公開資料，不需要登入 Google。
            </p>
          </div>
        </div>
      )}

      {hasReport && displayedReport ? (
        <>
          <ExecutionDetailsSection>{executionContent}</ExecutionDetailsSection>
          <div ref={reportRef} className="scroll-mt-4">
            <AuditReportView
              report={displayedReport}
              onRestart={handleRestart}
              saveStatus={saveStatus}
              saveError={saveError}
              saveReused={saveReused}
              onSave={handleSaveReport}
              reportToken={reportToken}
              readbackStatus={readbackStatus}
              readbackError={readbackError}
              onReadback={handleReadback}
              mode={viewMode}
              savedAt={savedReportMeta?.createdAt ?? ''}
              legacySavedReport={
                viewMode === 'saved' && savedReportMeta ? savedReportMeta.schemaVersion < 3 : false
              }
              onReturnToLive={handleReturnToLive}
              onOpenReportFromHistory={handleOpenReportFromHistory}
            />
          </div>
        </>
      ) : (
        <>
          {executionContent}
          {!categoryConfirmationRequired && !peerHintRequired && (
            <ScoreSummarySection
              status={scoreStatus}
              result={auditScoreResult}
              errorMessage={scoreError}
              onRetry={handleRetryScoring}
            />
          )}
        </>
      )}

      {!hasReport && (
        <>
          <StepsSection />
          <DataLimitSection />
        </>
      )}
    </SiteLayout>
  );
}